"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { type CircleActionVariant } from "./circle-action-variants";
import { LiquidPill, type LiquidPillSize } from "./liquid-pill";
import { V2_MOTION } from "./motion";
import { Z_INDEX } from "./z-index";

export interface LiquidPillExpandButtonProps {
  /** Icono que se muestra siempre dentro de la pill circular. */
  icon: React.ReactNode;
  /** Texto que se revela al hacer hover/focus en el botón. */
  label: React.ReactNode;
  /** Color semántico del LiquidPill (sigue las variants del sistema). */
  variant?: CircleActionVariant;
  /** Tamaño canónico (`sm` = 28px, `md` = 36px). */
  size?: LiquidPillSize;
  /** Handler de click — el botón ejecuta una acción única, no abre menú. */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Si la acción ya está activa, mantiene la pill en estado tinted permanente. */
  active?: boolean;
  /** Deshabilitar la pill. */
  disabled?: boolean;
  /**
   * Dirección hacia la que se extiende el label al expandirse.
   * - `right` (default): icono fijo a la izquierda, texto crece **hacia la derecha**.
   * - `left`: icono fijo a la derecha, texto crece **hacia la izquierda**. Usar para
   *   CTAs pegados al borde derecho del contenedor (header con CTA al extremo,
   *   esquinas de cards, etc.) para que el reveal no desborde.
   */
  expandTo?: "left" | "right";
  /** ARIA label para accesibilidad (el label visible solo aparece en hover). */
  "aria-label"?: string;
}

const SIZE_BOX: Record<Exclude<LiquidPillSize, "card-cta">, { size: number; iconSize: number }> = {
  xxs: { size: 20, iconSize: 10 },
  xs: { size: 24, iconSize: 12 },
  sm: { size: 28, iconSize: 14 },
  md: { size: 36, iconSize: 16 },
  lg: { size: 44, iconSize: 18 },
  xl: { size: 56, iconSize: 20 },
  "2xl": { size: 64, iconSize: 22 },
};

const OPEN_MS  = 440;
const CLOSE_MS = 220;
// Reveal premium: crecimiento amplio sin rebote gomoso.
const EASE     = V2_MOTION.ease.premiumReveal;

/**
 * Botón circular que revela su label horizontalmente al pasar el mouse por encima.
 *
 * **Modelo de posicionamiento dual** (cumple regla del design language §6.5 D):
 *
 * - **Inactivo (colapsado)**: el `<LiquidPill>` interno vive con `position: static`,
 *   en su posición natural del flujo del documento. **No** se usa `position: fixed`
 *   ni `position: absolute`. No se eleva en el stacking context. No tiene z-index
 *   propio. Por tanto **nunca se proyecta sobre el navbar ni sobre ningún elemento
 *   de capa superior** mientras esté en idle.
 *
 * - **Activo (expandido en hover/focus)**: el pill cambia a `position: absolute`
 *   dentro de un wrapper relativo que reserva el slot de layout. Sólo en ese
 *   estado recibe `z-index: Z_INDEX.DROPDOWN` (3000) para que el label revelado
 *   pase por encima del contenido adyacente, **pero por debajo del navbar**
 *   (`Z_INDEX.NAVBAR = 6000`).
 *
 * Comparte la mec\u00e1nica visual con `LiquidPillDropdown` (misma superficie l\u00edquida,
 * misma curva de animaci\u00f3n) pero a diferencia del dropdown:
 *
 * - Es una **acci\u00f3n \u00fanica** (`onClick`), no abre un men\u00fa de items.
 * - Se expande **en hover/focus**, no en click.
 * - Al hacer click ejecuta la acci\u00f3n y el bot\u00f3n permanece en su estado actual
 *   (colapsado cuando el mouse se va).
 */
export function LiquidPillExpandButton({
  icon,
  label,
  variant = "slate",
  size = "md",
  onClick,
  active = false,
  disabled = false,
  expandTo = "right",
  "aria-label": ariaLabel,
}: LiquidPillExpandButtonProps) {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const expanded   = (hovered || focused) && !disabled;
  const box        = SIZE_BOX[size];
  const durationMs = expanded ? OPEN_MS : CLOSE_MS;
  // Padding lateral cuando est\u00e1 colapsado: distancia que centra el icono dentro
  // del c\u00edrculo. Cuando expandido, mantenemos ese mismo padding del lado del
  // icono y crecemos el del lado del texto.
  const iconInset  = (box.size - box.iconSize) / 2;

  return (
    // Wrapper de layout: reserva siempre el slot del bot\u00f3n colapsado en el flow
    // del documento. Su tama\u00f1o no cambia con el hover \u2014 cuando el pill se vuelve
    // absolute, simplemente sale del flow y extiende lateralmente sin empujar
    // a los hermanos. El wrapper tambi\u00e9n establece el positioning context
    // (`position: relative`) para el pill absolute.
    //
    // Los handlers de hover/focus viven aqu\u00ed. Como el `<LiquidPill>` siempre es
    // un DOM descendant del wrapper (tanto en static como en absolute), el mouse
    // moverse sobre el pill expandido **no** dispara `mouseleave` del wrapper.
    <span
      className={cn(
        "liquid-pill-expand-wrapper relative inline-block align-top",
        expanded && "liquid-pill-expand-wrapper--expanded",
      )}
      style={{ width: `${box.size}px`, height: `${box.size}px` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <LiquidPill
        as="button"
        type="button"
        active={active || expanded}
        // Desactivamos el `interactive` interno (squish `lp-liquid`) para no
        // chocar con la animaci\u00f3n de reveal. El cursor pointer y el ring de
        // focus los heredamos porque `as="button"` ya marca `isInteractive=true`
        // internamente.
        interactive={false}
        hoverBounce={false}
        hoverDeform={false}
        variant={variant}
        shape="pill"
        size={size}
        disabled={disabled}
        onClick={onClick}
        aria-label={
          ariaLabel ?? (typeof label === "string" ? label : "Action")
        }
        className={cn(
          "liquid-pill-expand-btn",
          // Override del justify centrado por defecto del LiquidPill. Sin `!`
          // — twMerge resuelve el conflicto con `justify-center` quedando con
          // el último (el mío).
          expandTo === "right" ? "justify-start" : "justify-end",
          expanded && "liquid-pill-expand-btn--solid liquid-pill-expand-btn--expanded",
        )}
        style={{
          // \u2500\u2500 Position dual: static cuando inactivo, absolute cuando expandido \u2500\u2500
          //
          // Inactivo: el pill vive con position default (`static`) y llena el
          // wrapper (100% \u00d7 100% = box.size \u00d7 box.size). No tiene z-index. Sigue
          // las reglas naturales de stacking del DOM \u2014 nunca aparece sobre el
          // navbar, modal, drawer, etc. estando en idle.
          //
          // Activo: cambia a absolute, anclado al borde correspondiente del
          // wrapper (left:0 para expandTo=right, right:0 para expandTo=left).
          // Sale del flow para crecer lateralmente sin empujar a los hermanos.
          // Solo en este estado eleva z-index a DROPDOWN (3000) \u2014 sigue por
          // debajo del NAVBAR (6000), MODAL (11000) y TOAST (12000).
          position: expanded ? "absolute" : "static",
          top:     expanded ? 0 : undefined,
          left:    expanded && expandTo === "right" ? 0 : undefined,
          right:   expanded && expandTo === "left"  ? 0 : undefined,
          zIndex:  expanded ? Z_INDEX.DROPDOWN : undefined,

          // \u2500\u2500 Dimensiones y reveal \u2500\u2500
          width:      expanded ? "auto" : "100%",
          minWidth:   "100%",
          maxWidth:   expanded ? "16rem" : "100%",
          height:     "100%",
          transform:  expanded ? "scale(1.035)" : "scale(1)",
          transformOrigin: expandTo === "right" ? "left center" : "right center",
          willChange: expanded ? "max-width, padding, transform" : undefined,

          // ── Padding (orden de keys CRÍTICO) ──
          // 1) `padding: 0` resetea los 4 lados, anulando el PILL_SIZES del
          //    LiquidPill (`px-3.5 py-[7px]` para md). Inline beats class
          //    siempre que la class no use `!important` — por eso eliminamos
          //    el `!p-0` del className.
          // 2) paddingLeft/paddingRight (longhand) van DESPUÉS del `padding`.
          //    En CSS, longhand-after-shorthand gana incluso dentro del mismo
          //    objeto `style`. Si invertimos este orden, `padding: 0` borra
          //    los longhand y el icono pierde el centrado.
          // 3) padding-top y padding-bottom quedan en 0 (el contenido se
          //    centra verticalmente con `items-center`; la altura la fija
          //    `height: 100%` = box.size).
          padding: 0,
          paddingLeft: expandTo === "right"
            ? `${iconInset}px`                          // icono ancla a la izq
            : (expanded ? "16px" : `${iconInset}px`),   // texto crece hacia la izq
          paddingRight: expandTo === "right"
            ? (expanded ? "16px" : `${iconInset}px`)    // texto crece hacia la der
            : `${iconInset}px`,                         // icono ancla a la der
          gap: expanded ? "8px" : "0px",
          transition: [
            `max-width ${durationMs}ms ${EASE}`,
            `padding-left ${durationMs}ms ${EASE}`,
            `padding-right ${durationMs}ms ${EASE}`,
            `gap ${durationMs}ms ${EASE}`,
            `transform ${durationMs}ms ${EASE}`,
            // Surface (background/box-shadow/color) lo maneja el LiquidPill
            // base: el cross-fade idle->active vive en la capa overlay
            // `.liquid-pill__surface-active` (opacity), y color/box-shadow
            // los anima la transition global de `.liquid-pill`. NO declaramos
            // `background` aqui porque CSS no interpola entre backgrounds con
            // `radial-gradient` distintos (es discrete).
          ].join(","),
        }}
      >
        <span
          aria-hidden="true"
          className="liquid-pill-expand-btn__shine pointer-events-none absolute inset-y-0 z-[2] w-[76%]"
          style={{
            left: expandTo === "right" ? "-72%" : undefined,
            right: expandTo === "left" ? "-72%" : undefined,
            opacity: expanded ? 1 : 0,
            transform: expanded
              ? `translateX(${expandTo === "right" ? "255%" : "-255%"}) skewX(-18deg)`
              : "translateX(0) skewX(-18deg)",
            transition: [
              `transform ${durationMs + 120}ms ${EASE}`,
              `opacity ${Math.min(durationMs, 260)}ms ${V2_MOTION.ease.standard}`,
            ].join(","),
          }}
        />

        {/* Contenido custom: icono fijo + label revelable */}
        <span className="relative z-[3] inline-flex items-center" style={{ gap: "inherit" }}>
          {expandTo === "right" ? (
            <>
              <IconSlot iconSize={box.iconSize}>{icon}</IconSlot>
              <RevealLabel expanded={expanded} duration={durationMs}>{label}</RevealLabel>
            </>
          ) : (
            <>
              <RevealLabel expanded={expanded} duration={durationMs}>{label}</RevealLabel>
              <IconSlot iconSize={box.iconSize}>{icon}</IconSlot>
            </>
          )}
        </span>
      </LiquidPill>

      <style jsx global>{`
        /* Override del overflow-hidden del LiquidPill base \u2014 necesitamos que el
           contenido se anime sin clipping, ya que el ancho cambia y el label
           aparece desde 0px. */
        .liquid-pill-expand-btn {
          overflow: hidden;
        }

        @keyframes liquid-pill-expand-wow-aura {
          0% {
            opacity: 0;
            transform: scaleX(0.72) scaleY(0.76);
            filter: blur(10px);
          }
          32% {
            opacity: 0.72;
            transform: scaleX(1.34) scaleY(1.42);
            filter: blur(14px);
          }
          100% {
            opacity: 0;
            transform: scaleX(1.62) scaleY(1.72);
            filter: blur(18px);
          }
        }

        @keyframes liquid-pill-expand-wow-rim {
          0%   { box-shadow: 0 0 0 0 rgba(var(--cab-shadow-rgb),0.00); }
          34%  { box-shadow: 0 0 0 6px rgba(var(--cab-shadow-rgb),0.22); }
          100% { box-shadow: 0 0 0 14px rgba(var(--cab-shadow-rgb),0.00); }
        }

        .liquid-pill-expand-wrapper--expanded::before {
          content: "";
          position: absolute;
          inset: -9px;
          pointer-events: none;
          border-radius: 9999px;
          background:
            radial-gradient(ellipse 78% 62% at 50% 50%, rgba(var(--cab-shadow-rgb),0.42) 0%, rgba(var(--cab-shadow-rgb),0.20) 38%, transparent 72%);
          animation: liquid-pill-expand-wow-aura 620ms cubic-bezier(0.16, 1, 0.3, 1) both;
          transform-origin: center;
          z-index: 0;
        }

        .liquid-pill-expand-btn__shine {
          position: absolute !important;
          z-index: 2 !important;
          border-radius: inherit;
          background: linear-gradient(
            100deg,
            transparent 0%,
            rgba(255,255,255,0.00) 14%,
            rgba(255,255,255,0.62) 44%,
            rgba(255,255,255,0.18) 58%,
            transparent 100%
          );
          mix-blend-mode: screen;
          filter: blur(0.25px);
        }

        /* Estado expandido del CTA: sólido con gradiente de variante.
           El reveal sigue siendo el mismo, pero el surface deja de verse
           líquido/tinted y pasa a botón principal sólido (como CTA real). */
        .liquid-pill-expand-btn--solid {
          color: #fff !important;
          background: var(--cab-active-bg) !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.42),
            inset 0 -1px 0 rgba(15,23,42,0.16),
            inset 0 0 0 1px rgba(255,255,255,0.22),
            0 8px 18px -12px rgba(var(--cab-shadow-rgb),0.62),
            0 22px 58px -22px rgba(var(--cab-shadow-rgb),0.50) !important;
        }

        .liquid-pill-expand-btn--expanded {
          filter: saturate(1.06) brightness(1.02);
          animation: liquid-pill-expand-wow-rim 560ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .liquid-pill-expand-btn--solid svg,
        .liquid-pill-expand-btn--solid img {
          color: currentColor !important;
          filter: none !important;
          opacity: 1 !important;
        }
      `}</style>
    </span>
  );
}

/* \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
/*  Subcomponentes internos                                                    */
/* \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */

function IconSlot({ iconSize, children }: { iconSize: number; children: React.ReactNode }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center leading-none"
      style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
    >
      {children}
    </span>
  );
}

function RevealLabel({
  expanded,
  duration,
  children,
}: {
  expanded: boolean;
  duration: number;
  children: React.ReactNode;
}) {
  return (
    <span
      className="overflow-hidden whitespace-nowrap text-[12.5px] font-semibold tracking-[-0.01em]"
      style={{
        maxWidth: expanded ? "12rem" : "0px",
        opacity:  expanded ? 1 : 0,
        transition: [
          `max-width ${duration}ms ${EASE}`,
          `opacity ${duration}ms ${EASE}`,
        ].join(","),
      }}
    >
      {children}
    </span>
  );
}
